import { useCallback, useEffect, useRef } from "react";
import { useHardwareBackContext } from "../navigation/HardwareBackContext";
import {
  allowsSystemExitOnBack,
  isAppWorkflowScreen,
  isTopLevelDestinationScreen,
  KPI_HOME_SCREEN,
} from "../navigation/hardwareBackPolicy";
import { hardwareBackLog } from "../navigation/hardwareBackDebug";

const TRAP_STATE_FLAG = "crewpilotBackTrap";

function buildTrapState(screen) {
  return {
    [TRAP_STATE_FLAG]: true,
    screen,
  };
}

function hasActiveTrap() {
  return Boolean(window.history.state?.[TRAP_STATE_FLAG]);
}

/**
 * Keep a single synthetic history entry in sync with the current screen.
 * replaceState when the trap already exists; pushState only to (re)install it.
 */
function syncTrapState(screen) {
  const url = window.location.href;
  const state = buildTrapState(screen);

  if (hasActiveTrap()) {
    window.history.replaceState(state, "", url);
    return;
  }

  window.history.pushState(state, "", url);
}

function installTrap(screen) {
  window.history.pushState(buildTrapState(screen), "", window.location.href);
}

/**
 * Central Android / system Back policy for CrewPilot.
 *
 * History strategy (single-entry trap):
 * - Mount: one pushState trap.
 * - Screen changes: replaceState on the trap entry (no stacking).
 * - popstate: run policy; if consumed, push one trap back; if not, allow exit.
 *
 * Priority:
 * 1. Global overlays (navigator, briefings)
 * 2. Registered workflow handlers (nested steps inside a screen)
 * 3. App-level workflow screens (same as onBack props)
 * 4. Top-level destinations → KPI Dashboard
 * 5. KPI / auth → allow browser exit
 */
export function useAppHardwareBack({
  screen,
  onNavigateToKpi,
  onAppWorkflowBack,
  overlays = {},
}) {
  const { consumeRegisteredBack } = useHardwareBackContext();
  const screenRef = useRef(screen);
  screenRef.current = screen;

  const overlaysRef = useRef(overlays);
  overlaysRef.current = overlays;

  const onNavigateToKpiRef = useRef(onNavigateToKpi);
  onNavigateToKpiRef.current = onNavigateToKpi;

  const onAppWorkflowBackRef = useRef(onAppWorkflowBack);
  onAppWorkflowBackRef.current = onAppWorkflowBack;

  const handleHardwareBack = useCallback(() => {
    const currentScreen = screenRef.current;
    const {
      dashboardNavigatorOpen,
      onCloseDashboardNavigator,
      guideBriefing,
      onCloseGuideBriefing,
      startupBriefing,
      onCloseStartupBriefing,
    } = overlaysRef.current;

    if (dashboardNavigatorOpen) {
      onCloseDashboardNavigator?.();
      return true;
    }

    if (guideBriefing) {
      onCloseGuideBriefing?.();
      return true;
    }

    if (startupBriefing) {
      onCloseStartupBriefing?.();
      return true;
    }

    if (consumeRegisteredBack()) {
      return true;
    }

    if (isAppWorkflowScreen(currentScreen)) {
      onAppWorkflowBackRef.current?.(currentScreen);
      return true;
    }

    if (
      isTopLevelDestinationScreen(currentScreen) &&
      currentScreen !== KPI_HOME_SCREEN
    ) {
      onNavigateToKpiRef.current?.();
      return true;
    }

    if (allowsSystemExitOnBack(currentScreen)) {
      hardwareBackLog("handleHardwareBack → false (exit allowed)", {
        currentScreen,
      });
      return false;
    }

    onNavigateToKpiRef.current?.();
    hardwareBackLog("handleHardwareBack → true (KPI fallback)", {
      currentScreen,
    });
    return true;
  }, [consumeRegisteredBack]);

  const handleHardwareBackRef = useRef(handleHardwareBack);
  handleHardwareBackRef.current = handleHardwareBack;

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    installTrap(screenRef.current);

    const onPopState = () => {
      const currentScreen = screenRef.current;
      const destination = isTopLevelDestinationScreen(currentScreen);
      const workflow = isAppWorkflowScreen(currentScreen);
      const exitAllowed = allowsSystemExitOnBack(currentScreen);
      const trapPresent = hasActiveTrap();

      const consumed = handleHardwareBackRef.current();

      hardwareBackLog("popstate", {
        currentScreen,
        destination,
        workflow,
        exitAllowed,
        historyTrapPresent: trapPresent,
        historyStateScreen: window.history.state?.screen ?? null,
        historyLength: window.history.length,
        consumed,
      });

      if (consumed) {
        installTrap(screenRef.current);
      }
    };

    window.addEventListener("popstate", onPopState);

    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    syncTrapState(screen);
  }, [screen]);
}
